/**
 * Pi Chat ui_action tool — vibe-designer (2026-07-01; tiling-aware 1.424.0 P3).
 *
 * Lets the Pi agent INTERACT with the module/view the user is currently
 * looking at, on command ("switch to the pipeline tab", "change the theme to
 * aurora", "refresh the contacts"). Dispatches a `vibe:module-action`
 * CustomEvent that the active module listens for — the same window-event bus
 * `spa_action.focus_panel` already uses (voiceStore.applyVoiceSpaAction). The
 * voice agent reaches the identical bus via its `spa_action` `module_action`
 * verb, so voice and Pi drive the UI through one mechanism.
 *
 * P3 (concurrent module tiling): an optional `module_id` targets a specific
 * open tile (untargeted verbs go to the FOCUSED tile). The host itself
 * listens for the tiling verbs open_module / close_module / focus_module /
 * dock_module {edge} / maximize_module, so the agent can also arrange the
 * screen ("open comfyui next to the peptide project, dock it left").
 *
 * Scope: these are LOCAL, benign UI actions (change tab / theme / refresh /
 * open the external CRM / arrange tiles). They mutate nothing on the server
 * and reach no one, so they run immediately. Actual CRM WRITES stay on the
 * confirm-gated GHL tools (ghlTools.ts / gatedGhlWrite) — this tool never
 * touches them.
 *
 * Which per-module verbs exist is NOT hard-coded here — each open module
 * advertises its own verbs on its "[On screen now] … actions (ui_action verbs):"
 * line (single source: components/modules/moduleActions.ts, attached in
 * getSpaContext). Read that line to know what applies. Only the host-level
 * tiling verbs (always available) are listed statically below.
 */
import type { AgentTool, AgentToolResult } from '@earendil-works/pi-agent-core';
import { Type, type Static } from 'typebox';
import { getSpaContext, isMultiSpaContext } from '@/stores/spaContextStore';
import { TILING_ACTIONS, formatActionsLine } from '@/components/modules/moduleActions';

const uiActionSchema = Type.Object({
  action: Type.String({
    description:
      "The UI action to perform. PER-MODULE verbs are listed on each open module's " +
      "'[On screen now] … actions (ui_action verbs):' line — read them there and use one " +
      "(e.g. Mailcow 'set_tab' {tab: mailboxes|aliases}; Mattermost 'select_channel' {name}). " +
      "View verb: Terminals 'set_terminals_view' {view: 'terminals'|'hermes'} (flip the " +
      "Terminals page between the agent terminals and the embedded Hermes GUI). " +
      "Designer preview running a Blockworld voxel app: 'blockworld_cmd' {cmd} is the ONLY " +
      "way to build or change anything in it — do NOT use write_file / javascript_repl / edit " +
      "App.tsx; those do not touch the running app. The cmd goes to the app's command bar and " +
      "its OUTPUT IS RETURNED TO YOU, so use it as a REPL: start with cmd:'help' to see every " +
      "command, cmd:'world_info' to inspect. Build with cmd:'agent <request>' (natural language) " +
      "or cmd:'run [{\"op\":\"place_box\",\"args\":[0,0,0,4,3,4,\"stone\"]}]' (batch ops). " +
      "SCENES (add/save/load): cmd:'scenes' (list), cmd:'save_scene <name>', cmd:'load_scene " +
      "<name>', cmd:'fetch_scene <name>' — never write a scene file to disk, scenes live at " +
      "runtime. NEVER invent verbs — there is no set_block/place verb; ALL edits go through " +
      `blockworld_cmd. Tiling verbs handled by the host (always available): ${formatActionsLine(TILING_ACTIONS)}.`,
  }),
  params: Type.Optional(
    Type.Record(Type.String(), Type.Unknown(), {
      description: "Action arguments, e.g. { tab: 'pipeline' } or { edge: 'left' }.",
    }),
  ),
  module_id: Type.Optional(
    Type.String({
      description:
        'Target a specific OPEN module tile (ids are listed in the [On screen now] ' +
        'block). Omit to act on the focused tile.',
    }),
  ),
});

/** Dispatch a UI action to the active module and return a short confirmation.
 * Exported for direct unit testing without the Pi runtime. */
export function dispatchUiAction(
  action: string,
  params: Record<string, unknown>,
  moduleId?: string,
): string {
  window.dispatchEvent(
    new CustomEvent('vibe:module-action', {
      detail: { action, params, module_id: moduleId },
    }),
  );
  const ctx = getSpaContext();
  const where = moduleId
    ? `module ${moduleId}`
    : ctx
      ? isMultiSpaContext(ctx)
        ? `the focused module (${ctx.focusedId ?? 'unknown'})`
        : (ctx.title ?? 'the active view')
      : 'the active view';
  return `Dispatched UI action "${action}"${
    Object.keys(params).length ? ` (${JSON.stringify(params)})` : ''
  } to ${where}. The screen updates immediately; the next snapshot will reflect it.`;
}

/** Map an incoming ui_action to a Blockworld command string, or null when it is
 * NOT for the Blockworld preview. Accepts the correct form
 * (blockworld_cmd {cmd}) AND weaker models' fabrication: they drop the wrapper
 * and send the command AS the action itself (live-observed on cyankiwi:
 * {action:'scenes'} instead of {action:'blockworld_cmd', params:{cmd:'scenes'}};
 * likewise {action:'save_scene', params:{name:'x'}} → "save_scene x"). Recovery
 * fires ONLY when the Designer's Blockworld preview is the active on-screen
 * context (it advertises blockworld_cmd) and NEVER for host tiling verbs, so
 * legitimate open_module/dock_module still work. Blockworld's parser returns a
 * clean "Unknown op" for a genuinely-wrong guess — which now flows back to the
 * agent, so it self-corrects instead of looping. */
export function resolveBlockworldCmd(
  action: string,
  params: Record<string, unknown>,
): string | null {
  if (action === 'blockworld_cmd') {
    return typeof params.cmd === 'string' ? params.cmd : '';
  }
  if (TILING_ACTIONS.some((t) => t.verb === action)) return null;
  const ctx = getSpaContext();
  if (!ctx) return null;
  const view = isMultiSpaContext(ctx)
    ? ctx.modules[ctx.focusedId ?? Object.keys(ctx.modules)[0]]
    : ctx;
  const bwActive = !!view?.actions?.some((a) => a.verb === 'blockworld_cmd');
  if (!bwActive) return null;
  if (typeof params.cmd === 'string') return params.cmd;
  // fabricated bare verb → rebuild the command from action + primitive params
  const args = Object.values(params)
    .filter((v) => v !== null && v !== undefined && typeof v !== 'object')
    .map(String)
    .join(' ')
    .trim();
  return args ? `${action} ${args}` : action;
}

/** Blockworld's bridge is request/response: dispatch the cmd with a correlation
 * id and resolve with the app's ACTUAL command output (help text, scene list,
 * block counts, error messages) so the agent has a real feedback loop — it can
 * call cmd:'help' / cmd:'scenes' and READ the answer, then iterate. Falls back
 * gracefully when no Blockworld app is previewed (no reply within the timeout).
 * Exported for direct unit testing without the Pi runtime. */
export function dispatchBlockworldCmd(
  params: Record<string, unknown>,
  moduleId?: string,
): Promise<string> {
  const cmd = params.cmd;
  if (typeof cmd !== 'string' || !cmd.trim()) {
    return Promise.resolve('blockworld_cmd needs a { cmd } string, e.g. { cmd: "help" }.');
  }
  const reqId = `bw-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
  return new Promise<string>((resolve) => {
    let settled = false;
    // The app ACKs immediately (proving it's a live Blockworld app), then sends
    // the result. Two windows: short for the ack (catches "no Blockworld app"
    // fast), long for the result (an `agent`/`say` LLM build can take 10-45s).
    let resultTimer: ReturnType<typeof setTimeout> | undefined;
    const finish = (text: string) => {
      if (settled) return;
      settled = true;
      clearTimeout(ackTimer);
      if (resultTimer) clearTimeout(resultTimer);
      window.removeEventListener('vibe:blockworld-result', onEvent);
      resolve(text);
    };
    const onEvent = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | { id?: string; phase?: string; lines?: string[] }
        | undefined;
      if (!detail || detail.id !== reqId) return;
      if (detail.phase === 'ack') {
        clearTimeout(ackTimer);
        resultTimer = setTimeout(
          () =>
            finish(
              `Blockworld is still running "${cmd}" (a long build). It will finish in the app — ` +
                're-check with cmd:"world_info" in a moment.',
            ),
          90000,
        );
        return;
      }
      finish(
        (detail.lines ?? []).length
          ? `Blockworld ran "${cmd}". Output:\n${(detail.lines ?? []).join('\n').slice(0, 4000)}`
          : `Blockworld ran "${cmd}" (no output).`,
      );
    };
    window.addEventListener('vibe:blockworld-result', onEvent);
    const ackTimer = setTimeout(
      () =>
        finish(
          `Dispatched "${cmd}" to the Blockworld preview but it did not acknowledge. ` +
            'Is a Blockworld app open in the Designer preview and built? Switch to the Designer view and Build it, then retry.',
        ),
      6000,
    );
    window.dispatchEvent(
      new CustomEvent('vibe:module-action', {
        detail: { action: 'blockworld_cmd', params: { cmd }, module_id: moduleId, __reqId: reqId },
      }),
    );
  });
}

export function createUiActionTool(): AgentTool<typeof uiActionSchema, undefined> {
  return {
    name: 'ui_action',
    label: 'Control the on-screen UI',
    description:
      'Interact with the module/view the user is currently looking at — change its tab, ' +
      'theme, refresh it, open its external app, or ARRANGE the module tiles (open_module / ' +
      'close_module / focus_module / dock_module {edge} / maximize_module). Use when the user ' +
      'asks to DO something to the screen ("switch to the pipeline tab", "dock the peptide ' +
      'project left", "open the supabase module", "refresh the contacts"). Read the [On screen ' +
      'now] context block to know which modules are open (their ids target module_id) and what ' +
      'actions apply. Local + benign (no server writes, reaches no one) so it runs immediately. ' +
      'To WRITE to the CRM (create/edit/message a contact), use the confirm-gated GHL tools ' +
      'instead, NOT this tool.',
    parameters: uiActionSchema,
    execute: async (
      _toolCallId: string,
      params: Static<typeof uiActionSchema>,
    ): Promise<AgentToolResult<undefined>> => {
      // Blockworld is request/response — await the app's real output so the
      // agent sees help/scenes/errors, not a blind "dispatched". resolveBlockworldCmd
      // also recovers weaker models' fabricated bare verbs when the Blockworld
      // preview is active. All non-Blockworld verbs stay fire-and-forget.
      const bwCmd = resolveBlockworldCmd(params.action, params.params ?? {});
      const text =
        bwCmd !== null
          ? await dispatchBlockworldCmd({ cmd: bwCmd }, params.module_id)
          : dispatchUiAction(params.action, params.params ?? {}, params.module_id);
      return { content: [{ type: 'text', text }], details: undefined };
    },
  };
}
